// The tab's title (Michael, option 5): the most specific thing first, the instance last, unread chats in front, as
// Telegram does: "(3) Tasks · Studio404 — michael", a task open: "Fix login flow — michael". Most specific = an open
// modal's subject (a task, a member list …), else the page (a scoped view adds the picked space). Read from the URL,
// the board snapshot and the chat list (the core's chat.inbox, watched over the local replica: no extra requests).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { api, type Thread } from "@/api";
import { useBoardFilter } from "@/lib/hooks";
import { viewOf } from "@/lib/views";
import { useAuth, useData } from "@/store";
import { useViews } from "@/views";

const VIEW_KEY = { board: "nav.board", chats: "nav.chats", graph: "nav.graph", secrets: "nav.secrets", settings: "settings.title" } as const;

/** A chat's name as the Chats list shows it (a space's general chat: the space's). */
const threadName = (x?: Thread) => (x ? (x.general ? (x.spaceName ?? x.title) : x.title) : null);

export function useTitle() {
	const { t } = useTranslation();
	const { pathname, search } = useLocation();
	const [filter] = useBoardFilter();
	const snapshot = useData((s) => s.snapshot);
	const instance = useAuth((s) => s.instance);
	const [threads, setThreads] = useState<Thread[]>([]);
	useEffect(() => api.chat.inbox(null, (ts) => ts && setThreads(ts)), []);
	const unread = threads.reduce((n, x) => n + x.unread, 0);

	const q = new URLSearchParams(search);
	const navs = useViews().flatMap((v) => v.nav ?? []);
	const own = navs.find((n) => pathname === n.path || pathname.startsWith(`${n.path}/`));
	const view = viewOf(pathname, own ? [own.path] : []);
	// the page: a view, or the deepest list page of Settings (Instances, an instance, Ontologies)
	const segs = pathname.split("/").filter(Boolean).map(decodeURIComponent);
	const page =
		segs.at(-1) === "ontologies"
			? t("nav.ontologies")
			: segs[0] === "settings" && segs[2]
				? segs[2]
				: segs[0] === "settings" && segs[1] === "instances"
					? t("settings.instances")
					: view === "page"
						? (own?.label() ?? "")
						: t(VIEW_KEY[view]);
	// an open modal's subject wins (the filter and the scope picker are controls, not a subject)
	const id = q.get("id");
	const sec = q.get("sec");
	const modal =
		(id && (snapshot?.tasks.find((x) => x.id === id)?.title ?? t("detail.title", { defaultValue: "Details" }))) ||
		(q.get("pkg") ?? null) ||
		(sec ? (sec.split("|")[2] ?? sec) : null) ||
		(q.get("secret") ? t("secretAdd") : null) ||
		(q.get("members") ? t("settings.members") : null) ||
		(q.get("tokens") ? t("settings.tokens") : null) ||
		(q.get("sessions") ? t("sessions.title") : null) ||
		(q.get("connect") ? t("connections.connect") : null) ||
		(q.get("thread") ? threadName(threads.find((x) => x.id === q.get("thread"))) || t("chat.thread") : null);
	const scoped = !modal && view !== "settings" && filter.project ? snapshot?.projects.find((p) => p.id === filter.project)?.name : null;
	const title = `${unread ? `(${unread}) ` : ""}${modal ?? page}${scoped ? ` \u00b7 ${scoped}` : ""} \u2014 ${instance || t("app.title")}`;

	useEffect(() => {
		document.title = title;
	}, [title]);
}
